import _ from 'lodash';
import { createSelector } from 'reselect';

import {
  get,
  post,
  put,
  del, } from 'utils/fetch';
import { 
  createAction,
  entity_reducer,
  list_reducer,
  list_remove } from 'utils/store';
import { Storage, RequestState } from 'constants/constants';
import { timestampFilename } from 'utils/media';
import { uploadGCS } from 'utils/gcs';
import { createListSelector, listReducer, createList } from 'utils/list';

import { selectApiToken, selectCurrentUserId } from 'store/AppState';
import { selectCurrentAccountId } from 'store/models/Account';
import { FETCH_CHANNEL_SCENE_STATS, FETCH_CAMPAIGN_SCENE_STATS } from 'store/models/Stats';



// ======================
// HELPERS
// ====================== 
export const DefaultSettings = {
  length: 5000,
  pause: 0,
  background: '',
  backgroundOpacity: 100
}

export const getSceneLength = (baseSettings, sceneSettings) => sceneSettings?.length ?? baseSettings?.length ?? 5000;
export const getSceneStyle = (sceneSettings, baseSettings) => { // generally basesettings are null
  let background = sceneSettings?.background !== undefined 
    ? sceneSettings?.background 
    : baseSettings?.background ?? null;
  let backgroundOpacity = sceneSettings?.backgroundOpacity ?? baseSettings?.backgroundOpacity ?? 100;
  switch(background) {
    case "black":
      return { backgroundColor: `rgba(0,0,0,${backgroundOpacity / 100 || 1})` };
    case "white":
      return { backgroundColor: `rgba(256,256,256,${backgroundOpacity / 100 || 1})` };
  }
}

export const compareSettings = (sceneSettings, overlaySettings) => {
  return _.every(_.keys(overlaySettings), key => {
    if(_.isUndefined(sceneSettings[key]))
      return true;
    return _.isEqual(sceneSettings[key], overlaySettings[key]);
  })
}

export const isTransparent = (sceneSettings, baseSettings) => {
  let background = sceneSettings?.background !== undefined 
    ? sceneSettings?.background 
    : baseSettings?.background ?? null;
  return _.isEmpty(background);
}

// ======================
// ACTIONS
// ====================== 

export const FETCH_SCENES = 'FETCH_SCENES';
export const FETCH_SCENES_DELETED = 'FETCH_SCENES_DELETED';

export const FETCH_SCENES_CHANNEL = 'FETCH_SCENES_CHANNEL';
export const CREATE_SCENE = 'CREATE_SCENE';
export const UPDATE_SCENE = 'UPDATE_SCENE';
export const DELETE_SCENE = 'DELETE_SCENE';
export const RESTORE_SCENE = 'RESTORE_SCENE';
export const FETCH_SCENE_LIBRARY = 'FETCH_SCENE_LIBRARY';

export const MEDIA_UPLOAD_START = 'MEDIA_UPLOAD_START';
export const MEDIA_UPLOAD_SIG = 'MEDIA_UPLOAD_SIG';
export const MEDIA_UPLOAD = 'MEDIA_UPLOAD';
export const MEDIA_UPLOAD_COMPLETE = 'MEDIA_UPLOAD_COMPLETE';



// ======================
// SELECTORS
// ====================== 
export const selectAllScenes = (state) => state.models.scenes;
export const selectScenesByIds = createSelector(
  selectAllScenes,
  (s, sceneIds) => sceneIds,
  (scenes, sceneIds) => _.pick(scenes, sceneIds) |> _.values
)
export const selectScenes = (state, accountId) => selectScenesByAccount(state, accountId ?? selectCurrentAccountId(state))
export const selectDeletedScenes = (state, accountId) => state.models.scenesDeleted;
export const selectScenesByAccount = createListSelector('scenesByAccount');
export const selectScenesByUser = (state, id) => _.filter(state.models.scenes, m => (m.userId === id));
export const selectSceneLibrary = state => 
  state.models.library 
    |> _.mapValues(#, ids => selectScenesByIds(state, ids))

export const selectScenesForChannel = createListSelector('scenesForChannel');
export const selectScenesForCampaign = createListSelector('scenesForCampaign');
  



// ======================
// MODEL
// ====================== 
const Scene = {
  actions: {

    getSceneById: (id) => {
      return (dispatch, getState) => get({
        url: "/api/scene/:id", 
        params: { id },
        dispatch, 
        action: FETCH_SCENES,
      });
    },

    fetchScenes: accountId => (dispatch, getState) => {
      accountId = accountId ?? selectCurrentAccountId(getState());
      return get({
        url: '/api/account/:accountId/scenes',
        params: { accountId },
        dispatch,
        action: FETCH_SCENES,
      })
    },

    fetchDeletedScenes: accountId => (dispatch, getState) => {
      accountId = accountId ?? selectCurrentAccountId(getState());
      return get({
        url: '/api/account/:accountId/scenes/deleted',
        params: { accountId },
        dispatch,
        action: FETCH_SCENES_DELETED,
      })
    },

    fetchScenesForChannel: (channelId, from, to) => dispatch => get({
      url: '/api/scenes/channel/:channelId',
      params: { channelId, from, to },
      dispatch,
      action: FETCH_SCENES_CHANNEL,
    }),


    getScenesByOverlay: id => {
      return (dispatch, getState) => {
        let token = selectApiToken(getState());
        return get({
          url: "/api/scene/overlay/:id", 
          params: { id },
          dispatch, 
          action: FETCH_SCENES,
          headers: token ? { 'x-token': token,} : {},
        });
      }
    },

    getSceneLibrary: accountId => (dispatch, getState) =>
      accountId ?? selectCurrentAccountId(getState())
        |> get({
          url: "/api/account/:accountId/scene_library",
          params: { accountId: # },
          dispatch,
          action: FETCH_SCENE_LIBRARY,
        }),

    createScene: (values) => (dispatch, getState) => post({
      url: "/api/scene", 
      body: values,
      dispatch, 
      action: CREATE_SCENE,
    }),

    updateScene: (id, values) => (dispatch, getState) => put({
      url: "/api/scene/:id",
      params: { id },
      body: values,
      dispatch, 
      action: UPDATE_SCENE,
    }),

    deleteScene: sceneId => (dispatch, getState) => del({
      url: "/api/scene/:sceneId",
      params: { sceneId },
      dispatch, 
      action: DELETE_SCENE,
    }),

    restoreScene: (sceneId) => dispatch => put({
      url: '/api/scene/:sceneId/restore',
      params: { sceneId },
      body: {},
      dispatch,
      action: RESTORE_SCENE,
    }),

    getSignedUrl(userId, filetype) {
      return (dispatch, getState) => post({
        url: "/api/scene/sign",
        body: { filetype, userId },
        dispatch, 
        action: MEDIA_UPLOAD_SIG,
      });
    },

    uploadMedia: (file, cb) => async (dispatch, getState) => {
      if(!file) {
        console.warn("You must provide file and type");
        return;
      }

      let userId = selectCurrentUserId(getState());

      dispatch({type: MEDIA_UPLOAD_START});
      if(cb) 
        cb(MEDIA_UPLOAD_START); // Hacky but otherwise you need to have a store state just to track these states in UI

      let response = await dispatch(Scene.actions.getSignedUrl(userId, file.type)); 
      if(!response || response.status !== RequestState.SUCCESS) {
        return Promise.reject("problem with signature");
      }

      let { url, filename } = response.body;
      if(cb) 
        cb(MEDIA_UPLOAD_SIG, url);

      await uploadGCS(url, file);
      if(cb) 
        cb(MEDIA_UPLOAD, url, filename);

      dispatch({ type: MEDIA_UPLOAD_COMPLETE, response })
      if(cb) 
        cb(MEDIA_UPLOAD_COMPLETE, url, filename);
      return Promise.resolve(filename);
    },

    uploadToGCS: (url, file, mimetype) => (dispatch, getState) => put({
      url,
      params: { sceneId: id },
      body: file,
      dispatch, 
      action: DELETE_SCENE,
      header: {
        'Content-Type': mimetype,
      },
    }),
  },

  spec: {
    scenes: {},
    library: {},
    scenesDeleted: {},
  },

  reducer: (state, type, body, action, json) => {
    if (action.url && action.result !== RequestState.SUCCESS) 
      return state;

    if(body && body.scenes) {
      state = { ...state, 
        scenes: { ...state.scenes,
          ..._.keyBy(body.scenes, '_id'),
        }
      }
    }

    switch(type) {
      case FETCH_SCENE_LIBRARY:
        return {
          ...state,
          library: { ...body.library },
        }

      case FETCH_SCENES_DELETED:
      return { ...state,
        scenesDeleted: { ...state.scenesDeleted,
          ..._.keyBy(body.scenesDeleted, '_id'), 
        }
      }

      case DELETE_SCENE: {
        return {...state,
          scenes: _.omit(state.scenes, body._id),
          scenesDeleted: { ...state.scenesDeleted,
            [action.params.sceneId]: state.scenes[action.params.sceneId],
          }
        }
      }

      case RESTORE_SCENE: {
        return {
          ...state,
          scenesDeleted:_.omit(state.scenesDeleted, action.params.sceneId),
        }
      } 
    }
    return state;
  },

  listSpec: {
    scenesByAccount: {},
    scenesForChannel: {},
    scenesForCampaign: {},
  },

  listReducer: (state, type, body, action, json) => {
    if(action.url && action.result !== RequestState.SUCCESS)
      return state;

    if(body && body.scenes) {
      state = {
        ...state,
        scenesByAccount: listReducer(state, 'scenesByAccount', body.scenes, 'accountId', 'scenes', '_id'),
      };
    }

    switch(type) {
      case FETCH_SCENES_CHANNEL:
      case FETCH_CHANNEL_SCENE_STATS:
        return { ...state,
          scenesForChannel: {
            ...state.scenesForChannel,
            [action.params.channelId]: createList(body.scenes, 'scenes', '_id')
          } 
        }
      case FETCH_CAMPAIGN_SCENE_STATS:
        return { ...state,
          scenesForCampaign: {
            ...state.scenesForCampaign,
            [action.params.campaignId]: createList(body.scenes, 'scenes', '_id')
          } 
        }
    }

    return state;
  }
}
export default Scene;
  